import type { ContentHash, ContentRef } from "@cogo/publisher-core";

export interface HostCapabilityDeclaration {
  capabilityId: `host.${string}`;
  operations: readonly string[];
  adapterRef: ContentRef;
  sideEffectClass: "none" | "reversible" | "external";
}

export type HostValueSchema =
  | { kind: "unknown" }
  | { kind: "scalar"; type: "string" | "number" | "boolean" | "null" }
  | { kind: "array"; items: HostValueSchema }
  | {
      kind: "object";
      properties: Readonly<Record<string, HostValueSchema>>;
      required: readonly string[];
    };

export interface HostRegistryHandlerDeclaration {
  capabilityId: `host.${string}`;
  selector: string;
  payloadSchema: HostValueSchema;
  handlerRef: ContentRef;
}

export interface HostStoreHandlerDeclaration {
  handler: string;
  payloadSchema: HostValueSchema;
  handlerRef: ContentRef;
}

export interface HostDatasourceDeclaration {
  datasourceId: string;
  contractRef: ContentRef;
  requestSchema: HostValueSchema;
  responseSchema: HostValueSchema;
  fixtureRef?: ContentRef;
}

export interface HostCapabilityManifestBody {
  schemaVersion: "cogo.host-capability-manifest.v1";
  hostId: string;
  hostBuildRef: ContentRef;
  contextSchemas: Readonly<
    Record<"props" | "event" | "env" | "route", HostValueSchema>
  >;
  capabilities: readonly HostCapabilityDeclaration[];
  registryHandlers: readonly HostRegistryHandlerDeclaration[];
  storeHandlers: readonly HostStoreHandlerDeclaration[];
  datasources: readonly HostDatasourceDeclaration[];
  authContractRefs: readonly ContentRef[];
  consentContractRefs: readonly ContentRef[];
  confirmationContractRefs: readonly ContentRef[];
  idempotencyContractRefs: readonly ContentRef[];
  secretsBoundaryRef: ContentRef;
}

export interface HostCapabilityManifestDocument
  extends HostCapabilityManifestBody {
  contentHash: `sha256:${string}`;
}

const HASH = /^sha256:[a-f0-9]{64}$/;
const HOST_CAPABILITY = /^host\.[A-Za-z0-9][A-Za-z0-9._-]*$/;
const SAFE_IDENTIFIER = /^[A-Za-z0-9][A-Za-z0-9._:/#*-]*$/;
const UNSAFE_KEYS = new Set(["__proto__", "prototype", "constructor"]);

function compare(left: string, right: string): number {
  return left < right ? -1 : left > right ? 1 : 0;
}

function nonJson(path: string): never {
  throw new Error(`HOST_CAPABILITY_MANIFEST_NON_JSON:${path}`);
}

function assertPlainJson(
  value: unknown,
  path = "$",
  ancestors = new Set<object>(),
): void {
  if (value === null || typeof value === "string" || typeof value === "boolean") {
    return;
  }
  if (typeof value === "number") {
    if (!Number.isFinite(value)) nonJson(path);
    return;
  }
  if (typeof value !== "object") nonJson(path);
  if (ancestors.has(value)) nonJson(path);

  const expectedPrototype = Array.isArray(value)
    ? Array.prototype
    : Object.prototype;
  if (Object.getPrototypeOf(value) !== expectedPrototype) nonJson(path);
  if (Object.getOwnPropertySymbols(value).length > 0) nonJson(path);

  const descriptors = Object.getOwnPropertyDescriptors(value);
  const nextAncestors = new Set(ancestors).add(value);
  if (Array.isArray(value)) {
    const expectedKeys = Array.from({ length: value.length }, (_, index) => String(index));
    const actualKeys = Object.keys(descriptors).filter((key) => key !== "length");
    if (
      actualKeys.length !== expectedKeys.length ||
      actualKeys.some((key, index) => key !== expectedKeys[index])
    ) {
      nonJson(path);
    }
  }
  for (const [key, descriptor] of Object.entries(descriptors)) {
    if (Array.isArray(value) && key === "length") continue;
    if (
      !("value" in descriptor) ||
      !descriptor.enumerable ||
      descriptor.get !== undefined ||
      descriptor.set !== undefined
    ) {
      nonJson(`${path}.${key}`);
    }
    assertPlainJson(descriptor.value, `${path}.${key}`, nextAncestors);
  }
}

function canonicalize(value: unknown): unknown {
  if (
    value === null ||
    typeof value === "string" ||
    typeof value === "boolean"
  ) {
    return value;
  }
  if (typeof value === "number") {
    if (!Number.isFinite(value)) throw new Error("NON_FINITE_NUMBER");
    return value;
  }
  if (Array.isArray(value)) return value.map(canonicalize);
  if (typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>)
        .filter(([, entry]) => entry !== undefined)
        .sort(([left], [right]) => compare(left, right))
        .map(([key, entry]) => [key, canonicalize(entry)]),
    );
  }
  throw new Error(`UNSUPPORTED_CANONICAL_VALUE:${typeof value}`);
}

function stableStringify(value: unknown): string {
  return JSON.stringify(canonicalize(value));
}

// Browser-neutral synchronous SHA-256. Keeping this dependency-free prevents the
// shared schema export from pulling a server-only crypto implementation into Studio.
function sha256Text(value: string): ContentHash {
  const bytes = new TextEncoder().encode(value);
  const bitLength = bytes.length * 8;
  const paddedLength = Math.ceil((bytes.length + 9) / 64) * 64;
  const padded = new Uint8Array(paddedLength);
  padded.set(bytes);
  padded[bytes.length] = 0x80;
  const view = new DataView(padded.buffer);
  view.setUint32(paddedLength - 8, Math.floor(bitLength / 0x100000000));
  view.setUint32(paddedLength - 4, bitLength >>> 0);

  const k = SHA256_CONSTANTS;
  let h0 = 0x6a09e667;
  let h1 = 0xbb67ae85;
  let h2 = 0x3c6ef372;
  let h3 = 0xa54ff53a;
  let h4 = 0x510e527f;
  let h5 = 0x9b05688c;
  let h6 = 0x1f83d9ab;
  let h7 = 0x5be0cd19;
  const words = new Uint32Array(64);

  for (let offset = 0; offset < paddedLength; offset += 64) {
    for (let index = 0; index < 16; index += 1) {
      words[index] = view.getUint32(offset + index * 4);
    }
    for (let index = 16; index < 64; index += 1) {
      const w15 = words[index - 15]!;
      const w2 = words[index - 2]!;
      const s0 = rotateRight(w15, 7) ^ rotateRight(w15, 18) ^ (w15 >>> 3);
      const s1 = rotateRight(w2, 17) ^ rotateRight(w2, 19) ^ (w2 >>> 10);
      words[index] = (words[index - 16]! + s0 + words[index - 7]! + s1) >>> 0;
    }

    let a = h0;
    let b = h1;
    let c = h2;
    let d = h3;
    let e = h4;
    let f = h5;
    let g = h6;
    let h = h7;
    for (let index = 0; index < 64; index += 1) {
      const s1 = rotateRight(e, 6) ^ rotateRight(e, 11) ^ rotateRight(e, 25);
      const choice = (e & f) ^ (~e & g);
      const temp1 = (h + s1 + choice + k[index]! + words[index]!) >>> 0;
      const s0 = rotateRight(a, 2) ^ rotateRight(a, 13) ^ rotateRight(a, 22);
      const majority = (a & b) ^ (a & c) ^ (b & c);
      const temp2 = (s0 + majority) >>> 0;
      h = g;
      g = f;
      f = e;
      e = (d + temp1) >>> 0;
      d = c;
      c = b;
      b = a;
      a = (temp1 + temp2) >>> 0;
    }
    h0 = (h0 + a) >>> 0;
    h1 = (h1 + b) >>> 0;
    h2 = (h2 + c) >>> 0;
    h3 = (h3 + d) >>> 0;
    h4 = (h4 + e) >>> 0;
    h5 = (h5 + f) >>> 0;
    h6 = (h6 + g) >>> 0;
    h7 = (h7 + h) >>> 0;
  }

  const hex = [h0, h1, h2, h3, h4, h5, h6, h7]
    .map((part) => part.toString(16).padStart(8, "0"))
    .join("");
  return `sha256:${hex}`;
}

function rotateRight(value: number, places: number): number {
  return (value >>> places) | (value << (32 - places));
}

const SHA256_CONSTANTS = new Uint32Array([
  0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b,
  0x59f111f1, 0x923f82a4, 0xab1c5ed5, 0xd807aa98, 0x12835b01,
  0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7,
  0xc19bf174, 0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc,
  0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da, 0x983e5152,
  0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147,
  0x06ca6351, 0x14292967, 0x27b70a85, 0x2e1b2138, 0x4d2c6dfc,
  0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
  0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819,
  0xd6990624, 0xf40e3585, 0x106aa070, 0x19a4c116, 0x1e376c08,
  0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f,
  0x682e6ff3, 0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208,
  0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
]);

export function hashHostCapabilityContent(value: unknown): ContentHash {
  assertPlainJson(value);
  return sha256Text(stableStringify(value));
}

function assertSafeIdentifier(value: unknown, label: string, allowStar = false): asserts value is string {
  if (allowStar && value === "*") return;
  if (
    typeof value !== "string" ||
    value.length === 0 ||
    !SAFE_IDENTIFIER.test(value) ||
    value.split(/[.:/#*-]/).some((part) => UNSAFE_KEYS.has(part))
  ) {
    throw new Error(`HOST_CAPABILITY_MANIFEST_UNSAFE_IDENTIFIER:${label}`);
  }
}

function assertContentRef(value: ContentRef, label: string): void {
  if (
    !value ||
    typeof value !== "object" ||
    Object.keys(value).some((key) => key !== "ref" && key !== "contentHash") ||
    typeof value.ref !== "string" ||
    value.ref.length === 0 ||
    !HASH.test(value.contentHash)
  ) {
    throw new Error(`HOST_CAPABILITY_MANIFEST_INVALID_CONTENT_REF:${label}`);
  }
}

function assertOnlyKeys(
  value: object,
  allowed: readonly string[],
  label: string,
): void {
  const allowedSet = new Set(allowed);
  if (Object.keys(value).some((key) => !allowedSet.has(key))) {
    throw new Error(`HOST_CAPABILITY_MANIFEST_UNKNOWN_FIELD:${label}`);
  }
}

function assertHostCapabilityId(value: unknown, label: string): asserts value is `host.${string}` {
  if (
    typeof value !== "string" ||
    !HOST_CAPABILITY.test(value) ||
    value.split(".").some((part) => UNSAFE_KEYS.has(part))
  ) {
    throw new Error(`HOST_CAPABILITY_MANIFEST_UNSAFE_IDENTIFIER:${label}`);
  }
}

function normalizeRef(value: ContentRef): ContentRef {
  return { ref: value.ref, contentHash: value.contentHash };
}

function refIdentity(value: ContentRef): string {
  return `${value.ref}\u0000${value.contentHash}`;
}

function uniqueSortedStrings(
  values: readonly string[],
  label: string,
  allowEmpty = false,
): string[] {
  values.forEach((value, index) => assertSafeIdentifier(value, `${label}[${index}]`));
  const sorted = [...values].sort(compare);
  if (new Set(sorted).size !== sorted.length) {
    throw new Error(`HOST_CAPABILITY_MANIFEST_DUPLICATE_OPERATION:${label}`);
  }
  if (!allowEmpty && sorted.length === 0) {
    throw new Error(`HOST_CAPABILITY_MANIFEST_EMPTY_OPERATION:${label}`);
  }
  return sorted;
}

function normalizeValueSchema(value: HostValueSchema, label: string): HostValueSchema {
  if (!value || typeof value !== "object") {
    throw new Error(`HOST_CAPABILITY_MANIFEST_INVALID_VALUE_SCHEMA:${label}`);
  }
  if (value.kind === "unknown") {
    assertOnlyKeys(value, ["kind"], label);
    return { kind: "unknown" };
  }
  if (value.kind === "scalar") {
    assertOnlyKeys(value, ["kind", "type"], label);
    if (!["string", "number", "boolean", "null"].includes(value.type)) {
      throw new Error(`HOST_CAPABILITY_MANIFEST_INVALID_VALUE_SCHEMA:${label}`);
    }
    return { kind: "scalar", type: value.type };
  }
  if (value.kind === "array") {
    assertOnlyKeys(value, ["kind", "items"], label);
    return { kind: "array", items: normalizeValueSchema(value.items, `${label}.items`) };
  }
  if (value.kind !== "object") {
    throw new Error(`HOST_CAPABILITY_MANIFEST_INVALID_VALUE_SCHEMA:${label}`);
  }
  assertOnlyKeys(value, ["kind", "properties", "required"], label);
  const entries = Object.entries(value.properties).sort(([left], [right]) => compare(left, right));
  for (const [key] of entries) assertSafeIdentifier(key, `${label}.properties`);
  const properties = Object.fromEntries(
    entries.map(([key, schema]) => [key, normalizeValueSchema(schema, `${label}.${key}`)]),
  );
  const required = uniqueSortedStrings(value.required, `${label}.required`, true);
  if (required.some((key) => !Object.prototype.hasOwnProperty.call(properties, key))) {
    throw new Error(`HOST_CAPABILITY_MANIFEST_REQUIRED_PROPERTY_MISSING:${label}`);
  }
  return { kind: "object", properties, required };
}

function uniqueRefs(values: readonly ContentRef[], label: string): ContentRef[] {
  values.forEach((value, index) => assertContentRef(value, `${label}[${index}]`));
  const sorted = values.map(normalizeRef).sort((left, right) => compare(refIdentity(left), refIdentity(right)));
  if (new Set(sorted.map(refIdentity)).size !== sorted.length) {
    throw new Error(`HOST_CAPABILITY_MANIFEST_DUPLICATE_CONTENT_REF:${label}`);
  }
  return sorted;
}

function assertUnique(rows: readonly unknown[], keys: readonly string[], error: string): void {
  const seen = new Set<string>();
  for (const row of rows) {
    const record = row as Record<string, unknown>;
    const identity = keys.map((key) => String(record[key])).join("\u0000");
    if (seen.has(identity)) throw new Error(error);
    seen.add(identity);
  }
}

export function normalizeHostCapabilityManifestBody(
  body: HostCapabilityManifestBody,
): HostCapabilityManifestBody {
  assertPlainJson(body);
  if (body.schemaVersion !== "cogo.host-capability-manifest.v1") {
    throw new Error("HOST_CAPABILITY_MANIFEST_SCHEMA_VERSION");
  }
  assertSafeIdentifier(body.hostId, "hostId");
  assertContentRef(body.hostBuildRef, "hostBuildRef");
  assertContentRef(body.secretsBoundaryRef, "secretsBoundaryRef");
  const contextKeys = Object.keys(body.contextSchemas).sort(compare);
  if (contextKeys.join(",") !== "env,event,props,route") {
    throw new Error("HOST_CAPABILITY_MANIFEST_CONTEXT_SCHEMA_CLOSURE");
  }

  assertUnique(body.capabilities, ["capabilityId"], "HOST_CAPABILITY_MANIFEST_DUPLICATE_CAPABILITY");
  const capabilities = body.capabilities.map((row, index) => {
    assertOnlyKeys(row, ["capabilityId", "operations", "adapterRef", "sideEffectClass"], `capabilities[${index}]`);
    assertHostCapabilityId(row.capabilityId, `capabilities[${index}].capabilityId`);
    assertContentRef(row.adapterRef, `capabilities[${index}].adapterRef`);
    if (!["none", "reversible", "external"].includes(row.sideEffectClass)) {
      throw new Error(`HOST_CAPABILITY_MANIFEST_INVALID_SIDE_EFFECT_CLASS:capabilities[${index}]`);
    }
    return {
      capabilityId: row.capabilityId,
      operations: uniqueSortedStrings(row.operations, `capabilities[${index}].operations`),
      adapterRef: normalizeRef(row.adapterRef),
      sideEffectClass: row.sideEffectClass,
    };
  }).sort((left, right) => compare(left.capabilityId, right.capabilityId));

  assertUnique(body.registryHandlers, ["capabilityId", "selector"], "HOST_CAPABILITY_MANIFEST_DUPLICATE_REGISTRY_HANDLER");
  const registryHandlers = body.registryHandlers.map((row, index) => {
    assertOnlyKeys(row, ["capabilityId", "selector", "payloadSchema", "handlerRef"], `registryHandlers[${index}]`);
    assertHostCapabilityId(row.capabilityId, `registryHandlers[${index}].capabilityId`);
    assertSafeIdentifier(row.selector, `registryHandlers[${index}].selector`, true);
    assertContentRef(row.handlerRef, `registryHandlers[${index}].handlerRef`);
    return {
      capabilityId: row.capabilityId,
      selector: row.selector,
      payloadSchema: normalizeValueSchema(row.payloadSchema, `registryHandlers[${index}].payloadSchema`),
      handlerRef: normalizeRef(row.handlerRef),
    };
  }).sort((left, right) => compare(`${left.capabilityId}\u0000${left.selector}`, `${right.capabilityId}\u0000${right.selector}`));

  assertUnique(body.storeHandlers, ["handler"], "HOST_CAPABILITY_MANIFEST_DUPLICATE_STORE_HANDLER");
  const storeHandlers = body.storeHandlers.map((row, index) => {
    assertOnlyKeys(row, ["handler", "payloadSchema", "handlerRef"], `storeHandlers[${index}]`);
    assertSafeIdentifier(row.handler, `storeHandlers[${index}].handler`);
    assertContentRef(row.handlerRef, `storeHandlers[${index}].handlerRef`);
    return {
      handler: row.handler,
      payloadSchema: normalizeValueSchema(row.payloadSchema, `storeHandlers[${index}].payloadSchema`),
      handlerRef: normalizeRef(row.handlerRef),
    };
  }).sort((left, right) => compare(left.handler, right.handler));

  assertUnique(body.datasources, ["datasourceId"], "HOST_CAPABILITY_MANIFEST_DUPLICATE_DATASOURCE");
  const datasources = body.datasources.map((row, index) => {
    assertOnlyKeys(row, ["datasourceId", "contractRef", "requestSchema", "responseSchema", "fixtureRef"], `datasources[${index}]`);
    assertSafeIdentifier(row.datasourceId, `datasources[${index}].datasourceId`);
    assertContentRef(row.contractRef, `datasources[${index}].contractRef`);
    if (row.fixtureRef) assertContentRef(row.fixtureRef, `datasources[${index}].fixtureRef`);
    return {
      datasourceId: row.datasourceId,
      contractRef: normalizeRef(row.contractRef),
      requestSchema: normalizeValueSchema(row.requestSchema, `datasources[${index}].requestSchema`),
      responseSchema: normalizeValueSchema(row.responseSchema, `datasources[${index}].responseSchema`),
      ...(row.fixtureRef ? { fixtureRef: normalizeRef(row.fixtureRef) } : {}),
    };
  }).sort((left, right) => compare(left.datasourceId, right.datasourceId));

  return {
    schemaVersion: body.schemaVersion,
    hostId: body.hostId,
    hostBuildRef: normalizeRef(body.hostBuildRef),
    contextSchemas: {
      props: normalizeValueSchema(body.contextSchemas.props, "contextSchemas.props"),
      event: normalizeValueSchema(body.contextSchemas.event, "contextSchemas.event"),
      env: normalizeValueSchema(body.contextSchemas.env, "contextSchemas.env"),
      route: normalizeValueSchema(body.contextSchemas.route, "contextSchemas.route"),
    },
    capabilities,
    registryHandlers,
    storeHandlers,
    datasources,
    authContractRefs: uniqueRefs(body.authContractRefs, "authContractRefs"),
    consentContractRefs: uniqueRefs(body.consentContractRefs, "consentContractRefs"),
    confirmationContractRefs: uniqueRefs(body.confirmationContractRefs, "confirmationContractRefs"),
    idempotencyContractRefs: uniqueRefs(body.idempotencyContractRefs, "idempotencyContractRefs"),
    secretsBoundaryRef: normalizeRef(body.secretsBoundaryRef),
  };
}

export function createHostCapabilityManifest(
  body: HostCapabilityManifestBody,
): HostCapabilityManifestDocument {
  const normalized = normalizeHostCapabilityManifestBody(body);
  return { ...normalized, contentHash: hashHostCapabilityContent(normalized) };
}

export function assertHostCapabilityManifest(
  value: HostCapabilityManifestDocument,
): void {
  assertPlainJson(value);
  if (!value || typeof value !== "object") {
    throw new Error("HOST_CAPABILITY_MANIFEST_INVALID_DOCUMENT");
  }
  const allowed = new Set([
    "schemaVersion", "hostId", "hostBuildRef", "contextSchemas", "capabilities",
    "registryHandlers", "storeHandlers", "datasources", "authContractRefs",
    "consentContractRefs", "confirmationContractRefs", "idempotencyContractRefs",
    "secretsBoundaryRef", "contentHash",
  ]);
  if (Object.keys(value).some((key) => !allowed.has(key))) {
    throw new Error("HOST_CAPABILITY_MANIFEST_UNKNOWN_FIELD");
  }
  if (!HASH.test(value.contentHash)) {
    throw new Error("HOST_CAPABILITY_MANIFEST_INVALID_CONTENT_HASH");
  }
  const { contentHash, ...body } = value;
  const normalized = normalizeHostCapabilityManifestBody(body);
  if (hashHostCapabilityContent(normalized) !== contentHash) {
    throw new Error("HOST_CAPABILITY_MANIFEST_CONTENT_HASH_MISMATCH");
  }
}
